import type { Language } from "@mountayaapp/types/api/api";

import chineseFlag from "./flags/chinese.svg";
import englishFlag from "./flags/english.svg";
import frenchFlag from "./flags/french.svg";
import germanFlag from "./flags/german.svg";
import italianFlag from "./flags/italian.svg";
import japaneseFlag from "./flags/japanese.svg";
import spanishFlag from "./flags/spanish.svg";

/**
 * FLAG_HEIGHT_PX is the rendered flag height. The source art is a square 160x160
 * SVG, so the rendered width tracks the height — sized to sit inline with the
 * `xs`/`sm` Mantine text without crowding the adjacent label in the language
 * picker.
 */
const FLAG_HEIGHT_PX = 18;

/**
 * FLAGS pairs every supported UI language with its waving-flag asset. The seven
 * flags share one cloth — the same fold geometry, wave silhouette, and lighting
 * angle — so a row of them reads as a matched set rather than mixed clip art;
 * only the colour regions differ per country. The map is total over Language, so
 * LanguageFlag always finds an asset; the English fallback exists only for a
 * hypothetical future Language added before its flag ships. Each SVG is imported
 * as a URL so the bundler emits it as a static asset drawn through an `<img>`.
 */
const FLAGS: Record<Language, string> = {
  de: germanFlag,
  en: englishFlag,
  es: spanishFlag,
  fr: frenchFlag,
  it: italianFlag,
  ja: japaneseFlag,
  zh: chineseFlag,
};

/**
 * LANGUAGE_ENDONYM is the single source of truth for each UI language's endonym
 * (its name in its own tongue), the standard label set for a language picker.
 * Endonyms are not translated, so they stay a constant rather than locale keys.
 * The key is the domain Language a localization tool validates against, so the
 * same code drives the flag, the label, and the dispatch without a parallel
 * string union.
 */
export const LANGUAGE_ENDONYM: Record<Language, string> = {
  de: "Deutsch",
  en: "English",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  ja: "日本語",
  zh: "中文",
};

/**
 * UI_LANGUAGES is the ordered set of languages the SPA ships a UI message bundle
 * for. Only English and French carry an interface translation, so every UI-language
 * picker (the studio preferences panel, the organization embed default, the account
 * preferences) offers exactly these two — distinct from the video overlay's seven
 * render languages. The domain Language type keeps the trigger flag, each option
 * flag, and the dispatch reading one typed code.
 */
export const UI_LANGUAGES = ["en", "fr"] as const satisfies readonly Language[];

/**
 * UI_LANGUAGE_OPTIONS is the shared {@link LanguageSelect} option list for the two
 * UI languages, built from {@link LANGUAGE_ENDONYM} so the labels stay the single
 * source of truth (each language's name in its own tongue, never localized). It is
 * the one constant the preferences panel, the organization default-display picker,
 * and the account preferences all read, so the offered set never drifts between
 * surfaces. The values are the domain Language the preferences tool validates
 * against.
 */
export const UI_LANGUAGE_OPTIONS: readonly { label: string; value: Language }[] = UI_LANGUAGES.map(
  (value) => ({ label: LANGUAGE_ENDONYM[value], value }),
);

/**
 * LanguageFlagProps name the language whose flag to render. The value is the
 * domain Language type the preferences tool validates against, so the same code
 * the picker passes drives both the trigger and each option without a parallel
 * string union.
 */
interface LanguageFlagProps {
  language: Language;
}

/**
 * LanguageFlag renders the waving flag for a UI language as a small square image,
 * used as the language picker's left section and inside each option row. The flag
 * is decorative, so `aria-hidden` and an empty `alt` keep assistive tech on the
 * always-present language label rather than announcing the country. Every offered
 * language ships a flag; any future Language added before its asset falls back to
 * the English flag rather than rendering blank.
 */
export const LanguageFlag = ({ language }: LanguageFlagProps) => (
  <img
    alt=""
    aria-hidden
    src={FLAGS[language] ?? englishFlag}
    style={{ display: "block", height: FLAG_HEIGHT_PX, width: "auto" }}
  />
);
